import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/layout/DashboardShell";
import { carrierNav } from "@/pages/carrier/nav";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Bot, Check, Sparkles, Zap, MapPin, Plus, Trash2, Gauge } from "lucide-react";

export default function AIDispatcher() {
  const [d, setD] = useState(null);
  const [picks, setPicks] = useState(null);
  const [loadingPicks, setLoadingPicks] = useState(false);
  const [lanes, setLanes] = useState([]);
  const [lf, setLf] = useState({ origin: "", destination: "" });
  const [maxDeadhead, setMaxDeadhead] = useState(0);
  useEffect(() => { api.get("/carrier/ai-dispatcher").then((r) => setD(r.data)); }, []);
  useEffect(() => { api.get("/carrier/lanes").then((r) => setLanes(r.data)); }, []);
  useEffect(() => { api.get("/carrier/preferences").then((r) => setMaxDeadhead(r.data.max_deadhead || 0)); }, []);

  const saveDeadhead = async () => {
    await api.post("/carrier/preferences", { max_deadhead: Number(maxDeadhead) || 0 });
    toast.success("Deadhead radius saved — your AI picks will respect it");
  };

  const addLane = async () => {
    if (!lf.origin || !lf.destination) return;
    await api.post("/carrier/lanes", lf);
    toast.success("Lane saved — your picks will prioritize it");
    setLf({ origin: "", destination: "" });
    api.get("/carrier/lanes").then((r) => setLanes(r.data));
  };
  const delLane = async (id) => { await api.delete(`/carrier/lanes/${id}`); api.get("/carrier/lanes").then((r) => setLanes(r.data)); };

  const getPicks = async () => {
    setLoadingPicks(true);
    try { const { data } = await api.post("/carrier/ai-load-ranking"); setPicks(data); }
    finally { setLoadingPicks(false); }
  };

  if (!d) return <DashboardShell nav={carrierNav}><div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-xd-orange" size={32} /></div></DashboardShell>;

  return (
    <DashboardShell nav={carrierNav}>
      <div className="flex items-center gap-3 mb-2">
        <span className="grid place-items-center w-11 h-11 rounded-lg bg-xd-blue/20 text-xd-blue"><Bot size={22} /></span>
        <h1 className="font-heading text-3xl font-black tracking-tight">Recommended For You</h1>
      </div>
      <p className="text-sm text-xd-muted mb-6">Loads ranked for your equipment, lanes, and rate — updated as new freight comes in.</p>

      {/* Saved lanes */}
      <div className="bg-xd-paper border border-xd-border rounded-xl p-6 mb-6">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2"><MapPin className="text-xd-gold" size={18} /> Your Saved Lanes</h2>
        <p className="text-sm text-xd-muted">Save the lanes you like to run — XD focuses your AI picks and alerts on this freight.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {lanes.map((ln) => (
            <span key={ln.id} data-testid={`lane-${ln.id}`} className="inline-flex items-center gap-2 bg-xd-surface border border-xd-border rounded-full pl-3 pr-2 py-1 text-sm">
              {ln.label || `${ln.origin} → ${ln.destination}`}
              <button data-testid={`del-lane-${ln.id}`} onClick={() => delLane(ln.id)} className="text-xd-dim hover:text-xd-red"><Trash2 size={14} /></button>
            </span>
          ))}
          {lanes.length === 0 && <span className="text-sm text-xd-dim">No saved lanes yet.</span>}
        </div>
        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-end">
          <Input data-testid="lane-origin" value={lf.origin} onChange={(e) => setLf({ ...lf, origin: e.target.value })} placeholder="From (e.g. Houston, TX)" className="bg-xd-surface border-xd-border sm:w-56" />
          <Input data-testid="lane-destination" value={lf.destination} onChange={(e) => setLf({ ...lf, destination: e.target.value })} placeholder="To (e.g. Dallas, TX)" className="bg-xd-surface border-xd-border sm:w-56" />
          <Button data-testid="lane-add-btn" onClick={addLane} disabled={!lf.origin || !lf.destination} className="bg-xd-gold hover:bg-xd-goldH text-xd-bg font-semibold rounded-xl"><Plus size={16} className="mr-1" /> Save Lane</Button>
        </div>
        <div className="mt-4 pt-4 border-t border-xd-border flex flex-col sm:flex-row sm:items-end gap-2">
          <div>
            <label className="text-xs text-xd-muted flex items-center gap-1"><Gauge size={13} className="text-xd-blue" /> Max deadhead — empty miles you'll drive <span className="text-xd-dim">(0 = no limit)</span></label>
            <Input data-testid="deadhead-input" type="number" min="0" value={maxDeadhead} onChange={(e) => setMaxDeadhead(e.target.value)} className="mt-1 bg-xd-surface border-xd-border sm:w-40" />
          </div>
          <Button data-testid="deadhead-save-btn" onClick={saveDeadhead} variant="outline" className="border-xd-border text-white hover:bg-xd-surface rounded-xl">Save radius</Button>
        </div>
      </div>

      {/* AI picks of the day */}
      <div className="bg-gradient-to-r from-xd-blue/15 via-xd-paper to-xd-paper border border-xd-blue/40 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Sparkles className="text-xd-gold" size={18} /> Best Loads For You Today</h2>
            <p className="text-sm text-xd-muted">AI-ranked from today's board using your equipment & lanes.</p>
          </div>
          <Button data-testid="ai-picks-btn" onClick={getPicks} disabled={loadingPicks} className="bg-xd-blue hover:bg-xd-blueH text-white font-bold rounded-xl">
            {loadingPicks ? <><Loader2 className="animate-spin mr-2" size={16} /> Ranking…</> : <><Zap size={16} className="mr-2" /> {picks ? "Refresh Picks" : "Get Today's AI Picks"}</>}
          </Button>
        </div>

        {picks && (
          <div data-testid="ai-picks-result" className="mt-5 space-y-3">
            {picks.powered_by === "rules" && <p className="text-xs text-xd-dim">Showing smart rule-based picks (AI temporarily busy).</p>}
            {picks.loads.map((l, i) => (
              <div key={l.id} data-testid={`ai-pick-${l.xd_number}`} className="flex items-center gap-4 bg-xd-surface border border-xd-border rounded-lg p-4">
                <span className="grid place-items-center w-8 h-8 rounded-full bg-xd-gold text-xd-bg font-heading font-black text-sm shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-xd-dim">{l.xd_number}</span>
                    <span className="text-xs font-bold text-xd-green">Score {l.ai_score}</span>
                  </div>
                  <p className="font-heading font-bold truncate">{l.origin_city}, {l.origin_state} → {l.dest_city}, {l.dest_state}</p>
                  <p className="text-xs text-xd-muted">${l.total_pay?.toLocaleString()} · ${l.rate_per_mile}/mi · {l.ai_reason}</p>
                </div>
                <Button asChild size="sm" className="bg-xd-orange hover:bg-xd-orangeH text-white shrink-0"><Link to={`/find-loads/${l.id}`}>View</Link></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {d.loads.map((l) => (
          <div key={l.id} data-testid={`dispatch-${l.xd_number}`} className="bg-xd-paper border border-xd-border rounded-xl p-6 hover:border-xd-orange/50 transition-colors">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="lg:w-48 flex lg:flex-col items-center gap-2 shrink-0">
                <div className="relative w-20 h-20 grid place-items-center">
                  <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="44" fill="none" stroke="#2D2D2D" strokeWidth="8" />
                    <circle cx="50" cy="50" r="44" fill="none" stroke="#34C759" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${(l.match_score / 100) * 276} 276`} />
                  </svg>
                  <span className="font-heading font-black text-lg text-xd-green">{l.match_score}</span>
                </div>
                <span className="text-[10px] uppercase tracking-wide text-xd-dim">XD Match Score</span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-xd-dim">{l.xd_number}</span>
                  {l.xd_exclusive && <span className="text-[10px] font-bold uppercase text-xd-orange">XD Exclusive</span>}
                </div>
                <h3 className="font-heading text-xl font-bold">{l.origin_city}, {l.origin_state} → {l.dest_city}, {l.dest_state}</h3>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <Stat label="Load Pay" value={`$${l.total_pay.toLocaleString()}`} />
                  <Stat label="Rate / Mile" value={`$${l.rate_per_mile}`} />
                  <Stat label="Loaded / DH" value={`${l.loaded_miles} / ${l.deadhead_miles}`} />
                  <Stat label="Total Miles" value={l.total_miles} />
                  <Stat label="Est. Fuel" value={`$${l.estimated_fuel}`} />
                  <Stat label="Est. Op Cost" value={`$${l.estimated_operating_cost}`} />
                  <Stat label="Est. Contribution" value={`$${l.estimated_contribution}`} accent />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {l.match_reasons.map((r) => (
                    <span key={r} className="inline-flex items-center gap-1 text-xs text-xd-green bg-xd-green/10 border border-xd-green/25 rounded-full px-2 py-0.5"><Check size={11} /> {r}</span>
                  ))}
                </div>
              </div>

              <div className="flex lg:flex-col gap-2 shrink-0">
                <Button asChild data-testid={`dispatch-view-${l.xd_number}`} className="bg-xd-orange hover:bg-xd-orangeH text-white"><Link to={`/find-loads/${l.id}`}>View</Link></Button>
                <Button asChild variant="outline" className="border-xd-border text-white hover:bg-xd-surface"><Link to={`/find-loads/${l.id}`}>Book</Link></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}

function Stat({ label, value, accent }) {
  return <div><p className="text-[10px] uppercase tracking-wide text-xd-dim">{label}</p><p className={`font-semibold ${accent ? "text-xd-green" : "text-white"}`}>{value}</p></div>;
}
